// frontend/src/components/TextToJournal/TextToJournal.js

import React, { useState, useEffect, useRef, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { TemplateContext } from "../../context/TemplateContext";
import TemplateSelector from '../TemplateSelector/TemplateSelector';
import JournalEditor from '../JournalEditor/JournalEditor';
import PatientSelector from '../PatientSelector/PatientSelector';
import apiClient from '../../apiClient';
import streamClient from '../../streamClient';
import TextareaAutosize from 'react-textarea-autosize';
import './TextToJournal.css';

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  try {
    const response = await apiClient.get(url, options);
    return response;
  } catch (error) {
    if (retries === 0) throw error;
    await new Promise((res) => setTimeout(res, delay));
    return fetchWithRetry(url, options, retries - 1, delay);
  }
};

function TextToJournal() {
  const { templateId, setTemplateId } = useContext(TemplateContext);
  const navigate = useNavigate();
  const [inputText, setInputText] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // GPT-streaming
  const [streamingSections, setStreamingSections] = useState([]);
  const [generatedSections, setGeneratedSections] = useState([]);

  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState('');

  // Visar vi JournalEditor istället för textinmatning?
  const [showJournal, setShowJournal] = useState(false);

  // Session ID för att kunna spara och autospara
  // URL-param (om användaren öppnar en sparad session via ?session_id=123)
  const [searchParams] = useSearchParams();
  const sessionIdFromURL = searchParams.get("session_id") || null;
  const [sessionId, setSessionId] = useState(sessionIdFromURL);

  // Datum för sessionen
  const [startDateTime, setStartDateTime] = useState(null);

  // Patient-val
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [clinicSettings, setClinicSettings] = useState({
    use_internal_journal: false,
    journal_system: 'Kaddio'
  });

  // ---------------------------------------------------------
  // 1) Hämta mallar
  // ---------------------------------------------------------
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const resp = await fetchWithRetry("/api/journalmallar", {}, 3, 2000);

        if (Array.isArray(resp.data)) {
          setTemplates(resp.data);

          // ① Om templateId finns redan (från session eller context) → välj den
          if (templateId) {
            const found = resp.data.find(t => t.template_id === templateId);
            if (found) setSelectedTemplate(found);
          }

          // ② Om inget templateId ännu → välj första och skriv in i Context
          if (!templateId && resp.data.length > 0) {
            setTemplateId(resp.data[0].template_id);
            setSelectedTemplate(resp.data[0]);
          }
        } else {
          setError("Fel vid hämtning av mallar. Försök igen.");
        }
      } catch (err) {
        console.error("Fel vid hämtning av mallar:", err);
        setError("Misslyckades med att hämta mallar. Försök igen senare.");
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [templateId]);

  useEffect(() => {
    if (!templateId || templates.length === 0) return;
    const found = templates.find(t => t.template_id === templateId);
    if (found) setSelectedTemplate(found);
  }, [templateId, templates]);

  // ---------------------------------------------------------
  // 1aa) Hämta klinik-inställningar
  // ---------------------------------------------------------
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setClinicSettings({
          use_internal_journal: resp.data.use_internal_journal || false,
          journal_system: resp.data.journal_system || 'Kaddio'
        });
      } catch (error) {
        console.error('Error fetching clinic settings:', error);
      }
    };

    fetchClinicSettings();
  }, []);

  // ---------------------------------------------------------
  // 1b) Hämta sparad session om URL har ?session_id
  // ---------------------------------------------------------
  useEffect(() => {
    if (!sessionIdFromURL) return;          // ingen param → inget att göra
    if (templates.length === 0) return;     // vänta tills mallar är laddade

    const fetchSession = async () => {
      try {
        const res = await apiClient.get(`/recorder/sessions/${sessionIdFromURL}`);
        const { transcription_text, template_id, created_at } = res.data;

        setSessionId(sessionIdFromURL);         // försäkrar state
        setInputText(transcription_text || ""); // 👈 fyll textfältet

        // Sätt datum från sessionen
        if (created_at) {
          setStartDateTime(new Date(created_at));
        }

        if (template_id) {
          setTemplateId(template_id);           // context
          const found = templates.find(t => t.template_id === template_id);
          if (found) setSelectedTemplate(found);
        }
      } catch (err) {
        console.error("Kunde inte hämta session:", err);
      }
    };

    fetchSession();
  }, [sessionIdFromURL, templates]);           // körs igen när templates finns

  // ---------------------------------------------------------
  // 2a) Fake UX Streaming Helper
  // ---------------------------------------------------------
  const animateSectionsAppearance = async (sections, setSections) => {
    /**
     * Visar sektioner en i taget med delay för bättre UX
     * Även om backend returnerar allt på en gång
     */
    const DELAY_BETWEEN_SECTIONS = 400; // ms mellan varje sektion

    for (let i = 0; i < sections.length; i++) {
      // Visa sektioner progressivt
      setSections(sections.slice(0, i + 1));

      // Vänta innan nästa sektion (skippa delay efter sista)
      if (i < sections.length - 1) {
        await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_SECTIONS));
      }
    }
  };

  // ---------------------------------------------------------
  // 2b) parseNote - SIMPLIFIED (trusts backend)
  // ---------------------------------------------------------
  const parseNote = (text, template = null) => {
    /**
     * BEST PRACTICE: Frontend presenterar data från backend AS-IS
     * Backend garanterar korrekt format via Structured Outputs
     * INGEN deduplicering, INGEN validering
     *
     * @param template - explicit mall att parsa mot (undviker stale
     *   selectedTemplate vid mallbyte). Faller tillbaka på selectedTemplate.
     */

    if (!text || !text.trim()) {
      return [];
    }

    const templateToUse = template || selectedTemplate;

    // Hämta tillåtna rubriker från template (för parsing)
    const allowedTitles = new Set();
    if (templateToUse?.sections) {
      templateToUse.sections.forEach(sec => {
        allowedTitles.add(sec.section_title.trim());
      });
    }

    const lines = text.split('\n');
    const secs = [];
    let current = null;
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line) {
        const isValidTitle = allowedTitles.size === 0 || allowedTitles.has(line);

        if (isValidTitle && !current) {
          // Starta ny sektion
          current = { title: line, content: '' };
          i++;

          // Hoppa över tomma rader efter rubrik
          while (i < lines.length && lines[i]?.trim() === '') i++;

          // Samla innehåll tills nästa rubrik eller tom rad
          while (i < lines.length) {
            const nextLine = lines[i].trim();

            // Ny rubrik? Avbryt
            if (nextLine && allowedTitles.has(nextLine)) {
              break;
            }

            // Tom rad efter innehåll? Avbryt
            if (!nextLine && current.content.trim()) {
              i++;
              break;
            }

            current.content += lines[i] + '\n';
            i++;
          }

          secs.push(current);
          current = null;
        } else if (current) {
          current.content += line + '\n';
          i++;
        } else {
          // Text utan rubrik
          if (secs.length === 0) {
            secs.push({ title: 'Okänt', content: line + '\n' });
          } else {
            secs[secs.length - 1].content += line + '\n';
          }
          i++;
        }
      } else {
        i++;
      }
    }

    return secs;
  };

  // ---------------------------------------------------------
  // 3) "Generera Journal"-knappen med FAKE UX STREAMING
  // ---------------------------------------------------------
  const handleGenerateJournal = async () => {
    if (!inputText.trim() || !selectedTemplate) {
      setError('Textfältet och mall kan inte vara tomt.');
      return;
    }

    setError('');
    setIsTranscribing(true);
    setStreamingSections([]);
    setGeneratedSections([]);
    setShowJournal(true);

    try {
      // Skapa en ny session om vi inte redan har en
      let sid = sessionId;
      if (!sid) {
        const resp = await apiClient.post(
          '/recorder/sessions/start',
          { template_id: selectedTemplate.template_id, recording: false },
          { headers: { 'Content-Type': 'application/json' } }
        );
        sid = resp.data.session_id;
        setSessionId(sid);
        // Sätt starttid till nu för nya sessioner
        setStartDateTime(new Date());
      }
      await apiClient.patch(
        `/recorder/sessions/${sid}/update_transcription`,
        { transcription_text: inputText }
      );

      // Backend streaming (samlar hela svaret)
      let accumulated = '';

      await streamClient.post(
        '/api/chatgpt/generate_journal',
        {
          text: inputText,
          template_id: selectedTemplate.template_id,
          session_id: sessionId,
        },
        (chunk) => {
          accumulated += chunk;
          const partial = parseNote(accumulated);
          setStreamingSections(partial);
        }
      );

      // Parse final result
      const finalSections = parseNote(accumulated.trim());

      // FAKE UX STREAMING: Visa sektioner en i taget
      setStreamingSections([]);
      await animateSectionsAppearance(finalSections, setStreamingSections);

      // När animationen är klar, sätt final state
      setGeneratedSections(finalSections);
      setStreamingSections([]);
      setIsTranscribing(false);

      // Markera session som completed (ingen inspelning)
      await apiClient.post(
        `/recorder/sessions/${sid}/complete`,
        {}, // en tom JSON-body
        { headers: { 'Content-Type': 'application/json' } }
      );

      // Spara GPT-resultat i sessionens generated_journal
      const combinedText = finalSections
        .map((sec) => `${sec.title}\n${sec.content.trim()}`)
        .join('\n\n');

      await apiClient.patch(`/recorder/sessions/${sid}/update_journal`, {
        generated_journal: combinedText,
      });
    } catch (err) {
      console.error('Fel vid generering av journal:', err);
      setIsTranscribing(false);
      setError('Fel vid generering av journalanteckning. Försök igen.');
    }
  };

  // ---------------------------------------------------------
  // 4) "Regenerera" med FAKE UX STREAMING
  // ---------------------------------------------------------
  const handleRegenerate = async (templateOverride = null) => {
    // Använd EXPLICIT mall om den skickas in (t.ex. vid mallbyte). Annars kan
    // selectedTemplate vara inaktuell i closuren eftersom React inte hunnit
    // re-rendera efter setSelectedTemplate → fel template_id skulle skickas.
    const templateToUse = templateOverride || selectedTemplate;

    if (!inputText.trim()) {
      setError('Ingen text att generera om');
      return;
    }
    if (!templateToUse) {
      setError('Ingen mall vald');
      return;
    }

    console.log('[TextToJournal regenerate] template_id sent:', templateToUse?.template_id,
      '| mall:', templateToUse?.template_name);

    setError('');
    setStreamingSections([]);
    setGeneratedSections([]);
    setIsTranscribing(true);

    if (sessionId) {
      await apiClient.patch(
        `/recorder/sessions/${sessionId}/update_transcription`,
        { transcription_text: inputText }
      );
    }

    try {
      // Backend streaming (samlar hela svaret)
      let accumulated = '';

      await streamClient.post(
        '/api/chatgpt/generate_journal',
        { text: inputText, template_id: templateToUse.template_id, session_id: sessionId },
        (chunk) => {
          accumulated += chunk;
          const partial = parseNote(accumulated, templateToUse);
          setStreamingSections(partial);
        }
      );

      // Parse final result
      const finalSecs = parseNote(accumulated.trim(), templateToUse);

      // FAKE UX STREAMING: Visa sektioner en i taget
      setStreamingSections([]);
      await animateSectionsAppearance(finalSecs, setStreamingSections);

      // När animationen är klar
      setGeneratedSections(finalSecs);
      setStreamingSections([]);
      setIsTranscribing(false);

      // Om du vill spara om i DB
      if (sessionId) {
        const combinedText = finalSecs
          .map((sec) => `${sec.title}\n${sec.content.trim()}`)
          .join('\n\n');
        await apiClient.patch(`/recorder/sessions/${sessionId}/update_journal`, {
          generated_journal: combinedText,
        });
      }
    } catch (err) {
      console.error('Fel vid generera om:', err);
      setIsTranscribing(false);
      setError('Fel vid regenerering av journal. Försök igen.');
    }
  };

  // ---------------------------------------------------------
  // 5) "Byt mall" (anropas av JournalEditor)
  // ---------------------------------------------------------
  const handleChangeTemplate = async (newTemplateId) => {
    const found = templates.find((t) => t.template_id === newTemplateId);
    if (!found) {
      setError('Mall ej hittad');
      return;
    }
    setSelectedTemplate(found);
    setTemplateId(newTemplateId);     // 👈 context-synk

    // uppdatera sessionen i backend om den finns
    if (sessionId) {
      await apiClient.patch(
        `/recorder/sessions/${sessionId}/update_template`,
        { template_id: newTemplateId }
      );
    }
    if (inputText.trim()) {           // 🔒 bara om text finns
      // Skicka NYA mallen explicit – annars läser handleRegenerate en inaktuell
      // selectedTemplate (closure) och regenererar mot fel mall.
      await handleRegenerate(found);
    } else {
      setError("Det finns ingen text att generera om – lägg till text först.");
    }
  };

  // ---------------------------------------------------------
  // 6) Callback för att uppdatera generatedSections från JournalEditor
  // ---------------------------------------------------------
  const handleUpdateGeneratedSections = (newSections) => {
    setGeneratedSections(newSections);
  };

  // ---------------------------------------------------------
  // 7) Hantera patientväljare
  // ---------------------------------------------------------
  // Patientvalet lever enbart i frontend-state — inspelningssessionen (Notes)
  // känner inte till patienten. Kopplingen sparas i patient_journal_entries
  // när journalen sparas till patient.
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
  };

  const handlePatientClear = () => {
    setSelectedPatient(null);
  };

  // ---------------------------------------------------------
  // 8) Render
  // ---------------------------------------------------------
  if (showJournal) {
    return (
      <JournalEditor
        sessionId={sessionId}
        inputText={inputText}
        selectedTemplate={selectedTemplate}
        templates={templates}
        streamingSections={streamingSections}
        generatedSections={generatedSections}
        isTranscribing={isTranscribing}
        onRegenerate={handleRegenerate}
        onChangeTemplate={handleChangeTemplate}
        onUpdateGeneratedSections={handleUpdateGeneratedSections}
        startDateTime={startDateTime}
        // Patient-props
        patientId={selectedPatient?.patient_id || null}
        selectedPatient={selectedPatient}
        onPatientChange={setSelectedPatient}
        useInternalJournal={clinicSettings.use_internal_journal}
        onJournalSaved={(entryId) => {
          console.log('Journal saved with entry_id:', entryId);
          if (selectedPatient?.patient_id) {
            navigate(`/patients/${selectedPatient.patient_id}?tab=journal`);
          }
        }}
      />
    );
  }

  return (
    <div className="text-to-journal-container">
      <h2>Text till Journal</h2>

      <TemplateSelector
        templates={templates}
        selectedTemplate={selectedTemplate}
        handleTemplateChange={(id) => {
          const found = templates.find((t) => t.template_id === parseInt(id));
          setSelectedTemplate(found);
          setTemplateId(found?.template_id);
        }}
        loadingTemplates={loadingTemplates}
        error={error}
      />

      {/* Patientväljare - visas endast om kliniken använder inbyggd journal */}
      {clinicSettings.use_internal_journal && (
        <div className="text-to-journal-patient-selector">
          <label>Koppla till patient (valfritt)</label>
          <PatientSelector
            selectedPatient={selectedPatient}
            onPatientSelect={handlePatientSelect}
            onClear={handlePatientClear}
            placeholder="Sök patient..."
          />
        </div>
      )}

      <div className="input-tab">
        <TextareaAutosize
          className="journal-textarea"
          placeholder="Skriv din text här..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          minRows={5}
          maxRows={10}
        />

        {error && <p className="error-message">{error}</p>}

        <button
          onClick={handleGenerateJournal}
          className="generate-button"
          disabled={isTranscribing}
        >
          {isTranscribing ? 'Genererar...' : 'Generera Journal'}
        </button>
      </div>
    </div>
  );
}

export default TextToJournal;
